import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '@/app/AuthContext.jsx';
import { extractList, extractTotal } from '@/lib/apiList';

// server-paginated table data source for the Ledger-based lists.
// Wraps useInfiniteQuery around the lawyer-API's { pagination, results } shape:
// server-side search + sort live in `params` (folded into the query key so a
// change refetches from page 1), and pages are appended as the user scrolls.
//
// Returns the flattened items, the true total (read from pagination.totalRecords
// on the first page, so it's known before every row has loaded) and the paging
// controls a list needs to lazy-load, so every table pages the same way.
//
//   fetcher({ page, pageSize, ...params }) → { pagination, results } | array
//
export function useLedgerQuery({
  resourceKey,
  fetcher,
  params = {},
  pageSize = 50,
  enabled = true,
  // Opt-in (default false = comportamiento histórico): cuando es true, los
  // errores de fetch NO se tragan en una página vacía — se propagan para que la
  // vista pueda mostrar un estado de error. Útil en superficies donde "vacío" y
  // "falló la carga" no deben confundirse (p. ej. pagos).
  surfaceErrors = false,
}) {
  // The active org is carried by the X-Organization-Id header, not the query
  // params, so it must be folded into the key — otherwise two orgs share one
  // cache entry and switching orgs serves stale rows. Optional-chained so the
  // hook still works outside an AuthProvider (tests, Storybook). It scopes the
  // key AND rides into the fetcher (REC-1114: every org-scoped wrapper accepts
  // an explicit organizationId and pins the header; the orgContext singleton
  // is only the fallback).
  const organizationId = useAuth()?.organizationId ?? null;
  const query = useInfiniteQuery({
    queryKey: [resourceKey, { organizationId, ...params, pageSize }],
    queryFn: ({ pageParam }) =>
      surfaceErrors
        ? fetcher({ page: pageParam, pageSize, ...params, organizationId })
        : fetcher({
            page: pageParam,
            pageSize,
            ...params,
            organizationId,
          }).catch(() => null),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) return undefined;
      const total = extractTotal(lastPage, extractList(lastPage));
      const loaded = allPages.reduce(
        (n, p) => n + (p ? extractList(p).length : 0),
        0,
      );
      return loaded < total ? allPages.length + 1 : undefined;
    },
    enabled,
  });

  const items = useMemo(
    () =>
      query.data
        ? query.data.pages.flatMap((p) => (p ? extractList(p) : []))
        : [],
    [query.data],
  );

  const total =
    query.data?.pages?.[0] != null
      ? extractTotal(query.data.pages[0], extractList(query.data.pages[0]))
      : 0;

  return {
    items,
    total,
    loadedCount: items.length,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    refetch: query.refetch,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
  };
}

export default useLedgerQuery;
